import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { baseColors } from '../../../../Themes/colors';
import { designPx } from './aiReviewScale';
import { ReviewTooltip, SOURCE_DOCUMENT_LABELS } from './OverviewFieldMatchSection';
import { MissingIcon, OVERLINE_SX, RuleTooltipContent, REVIEW_COLORS as C, SourceDocumentIcon, SquareIcon } from './OverviewSourceDocuments';
var hasValue = function (value) { return Boolean(value) && value !== '--'; };
var STATUS_STYLES = {
    review: {
        label: 'Review Advised',
        chipBorder: C.reviewBorder,
        chipBg: C.reviewBg,
        chipText: C.reviewText,
        chipIcon: C.reviewIcon,
        noteBorder: C.amber400,
        noteBg: C.amber50,
        noteIcon: C.amber500,
        noteText: C.amber700,
    },
    matched: {
        label: 'Matched',
        chipBorder: C.matchedBorder,
        chipBg: C.matchedBg,
        chipText: C.matchedText,
        chipIcon: C.matchedIcon,
        noteBorder: C.emerald400,
        noteBg: C.emerald50,
        noteIcon: C.emerald500,
        noteText: C.emerald700,
    },
};
var StatusChip = function (_a) {
    var matched = _a.matched, tooltip = _a.tooltip;
    var style = matched ? STATUS_STYLES.matched : STATUS_STYLES.review;
    var chip = (React.createElement(Stack, { direction: "row", alignItems: "center", sx: {
            flexShrink: 0,
            gap: designPx(4),
            px: designPx(6),
            py: designPx(4),
            borderRadius: designPx(6),
            border: "1px solid ".concat(style.chipBorder),
            backgroundColor: style.chipBg,
        } },
        React.createElement("span", { className: matched ? 'icon-Circle_Check' : 'icon-Warning', style: { fontSize: designPx(12), color: style.chipIcon } }),
        React.createElement(Typography, { sx: { fontSize: designPx(12), lineHeight: 1.66, fontWeight: 500, color: style.chipText, whiteSpace: 'nowrap' } }, style.label)));
    return tooltip ? React.createElement(ReviewTooltip, { title: tooltip }, chip) : chip;
};
var ReferenceTag = function () { return (React.createElement(Typography, { component: "span", sx: {
        borderRadius: designPx(4),
        backgroundColor: C.reference,
        color: baseColors.successPreset[30],
        px: designPx(4),
        py: designPx(1),
        fontSize: designPx(9),
        lineHeight: 1.5,
        fontWeight: 500,
    } }, "Reference")); };
var EllipsisValue = function (_a) {
    var value = _a.value;
    var textRef = useRef(null);
    var _b = useState(false), isTruncated = _b[0], setIsTruncated = _b[1];
    useEffect(function () {
        var element = textRef.current;
        if (element)
            setIsTruncated(element.scrollWidth > element.clientWidth);
    }, [value]);
    return (React.createElement(Tooltip, { title: value, placement: "top", arrow: true, disableInteractive: true, disableHoverListener: !isTruncated },
        React.createElement(Typography, { ref: textRef, noWrap: true, sx: { mt: designPx(2), fontSize: designPx(12), lineHeight: designPx(16), fontWeight: 500, color: C.gray800 } }, value)));
};
var DocumentRow = function (_a) {
    var label = _a.label, value = _a.value, _b = _a.isReference, isReference = _b === void 0 ? false : _b, _c = _a.failed, failed = _c === void 0 ? false : _c, onOpen = _a.onOpen;
    var isMissing = !hasValue(value);
    return (React.createElement(Stack, { direction: "row", alignItems: "center", onClick: onOpen, role: onOpen ? 'button' : undefined, tabIndex: onOpen ? 0 : undefined, onKeyDown: onOpen ? function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
            }
        } : undefined, sx: {
            minWidth: 0,
            gap: designPx(12),
            px: designPx(12),
            py: designPx(8),
            borderRadius: designPx(8),
            border: "1px solid ".concat(isReference ? C.teal200 : C.gray200),
            backgroundColor: 'rgba(249, 250, 251, 0.7)',
            cursor: onOpen ? 'pointer' : 'default',
            '&:focus-visible': { outline: "2px solid ".concat(C.gray800), outlineOffset: 1 },
        } },
        React.createElement(SquareIcon, { size: 32, radius: 6, iconSize: 16 },
            React.createElement(SourceDocumentIcon, { label: label })),
        React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
            React.createElement(Stack, { direction: "row", alignItems: "center", sx: { gap: designPx(6) } },
                React.createElement(Typography, { sx: OVERLINE_SX }, label),
                isReference && React.createElement(ReferenceTag, null)),
            React.createElement(EllipsisValue, { value: isMissing ? '--' : value })),
        React.createElement(Box, { sx: { width: designPx(24), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, isMissing ? (React.createElement(MissingIcon, null)) : (React.createElement(Box, { sx: {
                width: designPx(8),
                height: designPx(8),
                borderRadius: '50%',
                backgroundColor: failed ? C.reviewIcon : isReference ? baseColors.successPreset[30] : C.emerald400,
            } })))));
};
var OverviewRuleGridCard = function (_a) {
    var id = _a.id, title = _a.title, icon = _a.icon, systemValue = _a.systemValue, badgeTone = _a.badgeTone, reason = _a.reason, ruleLabel = _a.ruleLabel, _b = _a.systemPresent, systemPresent = _b === void 0 ? false : _b, sourceEntries = _a.sourceEntries, onSourceOpen = _a.onSourceOpen;
    var isMatched = badgeTone === 'matched';
    var style = isMatched ? STATUS_STYLES.matched : STATUS_STYLES.review;
    var entryFor = function (label) { return sourceEntries.find(function (entry) { return entry.sourceLabel === label; }); };
    var invoiceEntry = entryFor(SOURCE_DOCUMENT_LABELS.invoice);
    var licenseEntry = entryFor(SOURCE_DOCUMENT_LABELS.driversLicense);
    var rows = [
        hasValue(systemValue) || systemPresent
            ? { label: SOURCE_DOCUMENT_LABELS.documentPackage, value: systemValue, isReference: true, onOpen: onSourceOpen }
            : null,
        invoiceEntry
            ? { label: SOURCE_DOCUMENT_LABELS.invoice, value: invoiceEntry.value, isReference: false, failed: Boolean(invoiceEntry.mismatched), onOpen: invoiceEntry.onOpen }
            : null,
        licenseEntry
            ? { label: SOURCE_DOCUMENT_LABELS.driversLicense, value: licenseEntry.value, isReference: false, failed: Boolean(licenseEntry.mismatched), onOpen: licenseEntry.onOpen }
            : null,
    ].filter(function (row) { return row !== null; });
    var tooltipOrder = [SOURCE_DOCUMENT_LABELS.documentPackage, SOURCE_DOCUMENT_LABELS.driversLicense, SOURCE_DOCUMENT_LABELS.invoice];
    var tooltipColumns = tooltipOrder
        .map(function (label) { return rows.find(function (row) { return row.label === label; }); })
        .filter(function (row) { return row !== undefined; })
        .map(function (row) { return ({ header: row.isReference ? 'Reference' : row.label, value: row.value }); });
    var chipTooltip = reason || tooltipColumns.length > 0
        ? React.createElement(RuleTooltipContent, { reason: reason, columns: tooltipColumns })
        : undefined;
    return (React.createElement(Box, { id: id, sx: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minWidth: 0,
            backgroundColor: C.white,
            border: "1px solid ".concat(C.gray200),
            borderRadius: designPx(12),
        } },
        React.createElement(Box, { sx: { px: designPx(16), pt: designPx(12), pb: designPx(10), borderBottom: "1px solid ".concat(C.gray100) } },
            React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", sx: { columnGap: designPx(8), rowGap: designPx(6) } },
                React.createElement(Stack, { direction: "row", alignItems: "center", sx: { minWidth: 0, flex: '1 1 auto', gap: designPx(10) } },
                    React.createElement(SquareIcon, { size: 32, radius: 8, iconSize: 16, plain: true }, icon),
                    React.createElement(Typography, { sx: { fontSize: designPx(14), lineHeight: designPx(20), fontWeight: 700, color: C.gray800 } }, title)),
                React.createElement(StatusChip, { matched: isMatched, tooltip: chipTooltip })),
            ruleLabel && (React.createElement(Typography, { sx: { mt: designPx(4), fontSize: designPx(11), lineHeight: designPx(16), color: C.gray500 } },
                "Rule:",
                ' ',
                React.createElement(Typography, { component: "span", sx: { fontSize: 'inherit', lineHeight: 'inherit', fontWeight: 400, color: C.gray700 } }, ruleLabel)))),
        React.createElement(Stack, { sx: { gap: designPx(8), p: designPx(12) } }, rows.map(function (row) { return (React.createElement(DocumentRow, { key: row.label, label: row.label, value: row.value, isReference: row.isReference, failed: 'failed' in row ? row.failed : false, onOpen: row.onOpen })); })),
        React.createElement(Box, { sx: { mt: 'auto', px: designPx(12), pb: designPx(12) } })));
};
export default OverviewRuleGridCard;
